import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, LinearProgress, Chip, Divider, CircularProgress } from '@mui/material';
import DedicatedServers from './DedicatedServers';
import { getSystem, getWorkerStatus, setWorkerMode, setWorkerConcurrency, getCoreServers, getUpgradeOptions, upgradeDedicated } from '../api';
import { useDemo, DEMO_SERVER, DEMO_SPECS, DEMO_ACID, fakeLoad, fakeQueue } from '../demoMode';   // [D-423, D-430]

const colorByValue = (v) => {
  if (v === null || v === undefined) return '#666';
  if (v > 85) return '#f44336';
  if (v > 70) return '#ff9800';
  if (v > 50) return '#ffeb3b';
  return '#4caf50';
};

const MetricBar = ({ label, value, icon }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
      <Typography variant="caption" sx={{ color: '#aaa' }}>{icon} {label}</Typography>
      <Typography variant="caption" sx={{ color: colorByValue(value), fontWeight: 'bold' }}>
        {value !== null && value !== undefined ? value.toFixed(1) + '%' : '—'}
      </Typography>
    </Box>
    <LinearProgress variant="determinate" value={value || 0}
      sx={{ height: 6, borderRadius: 3, bgcolor: '#333',
        '& .MuiLinearProgress-bar': { bgcolor: colorByValue(value), borderRadius: 3 }}}/>
  </Box>
);

const InfoRow = ({ label, value }) => value ? (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="caption" sx={{ color: '#888' }}>{label}</Typography>
    <Typography variant="caption" sx={{ color: '#ddd' }}>{value}</Typography>
  </Box>
) : null;

const ServerCard = ({ title, ip, data: dataRaw, accent, serverKey, worker, onSet, onConc, hname, stype, disk }) => {
  const [ctOpen, setCtOpen] = React.useState(false);
  const [ctData, setCtData] = React.useState(null);
  const [ctLoading, setCtLoading] = React.useState(false);
  const [ctChosen, setCtChosen] = React.useState('');
  const [ctBusy, setCtBusy] = React.useState(false);
  const openCt = () => { const n = !ctOpen; setCtOpen(n); if (n && !ctData && stype) { setCtLoading(true); getUpgradeOptions(stype, disk).then(r => setCtData(r.data || {})).catch(() => setCtData({ error: 'не вдалось' })).finally(() => setCtLoading(false)); } };
  const doCt = () => { if (!ctChosen) return; if (!window.confirm('Змінити тип ' + hname + ' -> ' + ctChosen.toUpperCase() + '? Диск лишиться (можна повернути). Сервер на ~1-2 хв призупиниться — робити ВНОЧІ.')) return; setCtBusy(true); upgradeDedicated(hname, ctChosen).then(() => { alert('Запущено. Сервер зміниться за кілька хвилин.'); setCtOpen(false); }).catch(e => alert('Помилка: ' + (e.response?.data?.detail || e.message))).finally(() => setCtBusy(false)); };
  // [D-423] Демо-режим: показуємо дашборд сторонньому, не показуючи
  // справжніх моделей, цін і адрес.
  //
  // 🔴 Підміняємо ТІЛЬКИ відображення. Метрики (CPU, RAM, диск) лишаються
  // справжні: вони нічого не виказують, а підроблені графіки зробили б
  // демонстрацію брехнею замість стриманості.
  const demoOn = useDemo();
  // [D-430] У демо підміняємо і характеристики, і навантаження: OWNER
  // показує дашборд як вітрину, де система має виглядати завантаженою.
  //
  // 🔴 `plan` складаємо однією стрічкою через «|», бо саме так його вже
  // читає верстка: до першої риски — модель у чіпі, решта — підпис унизу.
  // Нової розмітки не заводимо, тож поза демо все лишається як було.
  const data = demoOn
    ? {
        ...dataRaw,
        ...fakeLoad(title || ip || 'srv'),
        plan: DEMO_SERVER.plan + ' | ' + DEMO_SPECS.cpu_model
              + ' | ' + DEMO_SPECS.ram_total_gb + ' ГБ ' + DEMO_SPECS.ram_type
              + ' | ' + DEMO_SPECS.disks,
        price: DEMO_SERVER.price,
      }
    : dataRaw;
  const ipShown = demoOn ? '•••.•••.•••.•••' : ip;
  if (!data) return null;
  const uptimeDays = data.uptime_hours ? Math.floor(data.uptime_hours / 24) : null;
  const uptimeHrs = data.uptime_hours ? Math.floor(data.uptime_hours % 24) : null;

  return (
    <Card sx={{ bgcolor: '#1a1a2e', border: `1px solid ${accent || '#333'}`, borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 'bold' }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>📡 {ipShown}</Typography>
          </Box>
          <Chip label={data.plan?.split('|')[0]?.trim() || '?'} size="small"
            sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.2)', color: 'var(--ac1, #7c4dff)', fontSize: 10 }}/>
        </Box>

        <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 1.5 }}>{data.role}</Typography>

        <MetricBar label="CPU" value={data.cpu} icon="🖥"/>
        <MetricBar label="RAM" value={data.ram} icon="🧠"/>
        <MetricBar label="Диск" value={data.disk} icon="💾"/>
        {data.storage_pct != null && <MetricBar label="Відео (сховище)" value={data.storage_pct} icon="🎬"/>}
        {data.nfs_disk != null && <MetricBar label="NFS відео" value={data.nfs_disk} icon="📦"/>}

        <Divider sx={{ borderColor: '#333', my: 1.5 }}/>

        <InfoRow label="🔧 vCPU" value={data.cpu_cores ? data.cpu_cores + ' ядер' : null}/>
        <InfoRow label="🧠 RAM" value={data.ram_total_gb ? data.ram_total_gb + ' GB' : null}/>
        <InfoRow label="💾 Диск вільно" value={data.disk_free_gb ? data.disk_free_gb + ' / ' + data.disk_total_gb + ' GB' : null}/>
        {data.storage_gb != null && <InfoRow label="🎬 Відео (локально, 24г)" value={data.storage_gb + ' GB'}/>}
        {data.nfs_free_gb != null && <InfoRow label={<span style={{color: data.nfs_free_gb < 5 ? '#f44336' : data.nfs_free_gb < 15 ? '#ff9800' : '#4caf50'}}>📦 NFS вільно</span>} value={data.nfs_free_gb + ' / ' + data.nfs_total_gb + ' GB'}/>}
        <InfoRow label="⏱ Uptime" value={uptimeDays !== null ? uptimeDays + 'д ' + uptimeHrs + 'г' : null}/>
        <InfoRow label="📊 Load" value={data.load_avg}/>
        {demoOn && <InfoRow label="🌐 Мережа" value={DEMO_SPECS.net}/>}
        {ip && data.concurrency != null && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#888' }}>⚡ Concurrency:</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[1, 2, 4].map(v => {
                const active = data.concurrency === v;
                return (
                  <Box key={v} onClick={() => !active && onConc && onConc(data.ip, v)}
                    sx={{ cursor: active ? 'default' : 'pointer', px: 1.3, py: 0.3, borderRadius: 1.5, fontSize: 13, userSelect: 'none',
                          bgcolor: active ? accent : 'rgba(255,255,255,0.06)', color: active ? '#000' : '#999',
                          fontWeight: active ? 'bold' : 'normal', border: `1px solid ${active ? accent : 'transparent'}`,
                          '&:hover': { bgcolor: active ? accent : 'rgba(255,255,255,0.12)' } }}>
                    {v}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Divider sx={{ borderColor: '#333', my: 1.5 }}/>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* [D-430] У демо ціна має впадати в око — вимога OWNER. Поза
              демо лишається тим самим сірим підписом, що й був. */}
          {demoOn ? (
            <Box sx={{ px: 1, py: .2, borderRadius: 1, bgcolor: DEMO_ACID,
                       boxShadow: `0 0 10px ${DEMO_ACID}`, lineHeight: 1.4 }}>
              <Typography variant="caption"
                sx={{ color: '#111', fontWeight: 900, letterSpacing: .3 }}>
                💰 {data.price}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: '#666' }}>💰 {data.price}</Typography>
          )}
          <Typography variant="caption" sx={{ color: data.plan?.includes('Disk') ? '#4caf50' : '#aaa' }}>
            📋 {data.plan?.split('|').slice(1).join('|')}
          </Typography>
        </Box>

        {serverKey && (
          <Box sx={{ mt: 1.5, bgcolor: 'rgba(0,0,0,0.25)', borderRadius: 2, px: 1.5, py: 0.8 }}>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
              🎬 Воркер {!worker?.online && <span style={{ color: '#f44336' }}>⚪ offline</span>}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[['off', '⛔ Вимк', '#f44336'], ['short', '📱 Короткі', '#ff9800'], ['all', '🎬 Всі', '#4caf50']].map(([m, label, col]) => {
                const active = worker?.mode === m;
                return (
                  <Box key={m} onClick={() => worker?.online && onSet && onSet(serverKey, m)}
                    sx={{ flex: 1, textAlign: 'center', cursor: worker?.online ? 'pointer' : 'default',
                          px: 0.5, py: 0.5, borderRadius: 1.5, fontSize: 11, userSelect: 'none',
                          bgcolor: active ? col : 'rgba(255,255,255,0.06)',
                          color: active ? '#000' : '#999', fontWeight: active ? 'bold' : 'normal',
                          border: `1px solid ${active ? col : 'transparent'}`,
                          opacity: worker?.online === false ? 0.4 : 1,
                          '&:hover': { bgcolor: active ? col : 'rgba(255,255,255,0.12)' } }}>
                    {label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        {hname && (
          <>
            <Divider sx={{ borderColor: '#333', my: 1 }}/>
            <Box onClick={openCt} sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#4caf50', fontSize: 13, userSelect: 'none' }}>
              <span>🔧 Змінити тип{stype ? ' (' + stype.toUpperCase() + ')' : ''}</span>
              <span>{ctOpen ? '▴' : '▾'}</span>
            </Box>
            {ctOpen && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', mb: 1 }}>⚠️ зміна = пауза ~1-2 хв, робити ВНОЧІ (диск лишається, зворотно)</Typography>
                {ctLoading ? (
                  <LinearProgress sx={{ bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' } }}/>
                ) : ctData?.error ? (
                  <Typography variant="caption" sx={{ color: '#f44336' }}>{ctData.error}</Typography>
                ) : ctData?.options?.length ? (
                  <>
                    {ctData.options.map(o => {
                      const sel = ctChosen === o.name;
                      return (
                        <Box key={o.name} onClick={() => setCtChosen(o.name)}
                          sx={{ cursor: 'pointer', p: 0.8, mb: 0.5, borderRadius: 1.5, fontSize: 12,
                                border: `1px solid ${sel ? '#4caf50' : '#333'}`, bgcolor: sel ? 'rgba(76,175,80,0.15)' : 'transparent',
                                display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#ddd' }}>{sel ? '🟢 ' : ''}{o.name.toUpperCase()} · {o.cores} яд · ~{o.comfy_users} юз {o.cheaper ? '↩' : ''}</span>
                          <span style={{ color: o.cheaper ? '#4fc3f7' : '#4caf50' }}>€{o.price}</span>
                        </Box>
                      );
                    })}
                    {ctChosen && (
                      <Box onClick={() => !ctBusy && doCt()} sx={{ mt: 1, textAlign: 'center', cursor: 'pointer', p: 0.7, borderRadius: 1.5, bgcolor: '#4caf50', color: '#000', fontWeight: 'bold', fontSize: 12, opacity: ctBusy ? 0.5 : 1 }}>
                        {ctBusy ? 'Запуск…' : '🔧 Змінити на ' + ctChosen.toUpperCase()}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" sx={{ color: '#aaa' }}>нема доступних варіантів зараз</Typography>
                )}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default function SystemPage() {
  const TOTAL_SERVERS = 6;
  const [sysOrder, setSysOrder] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sys_order'));
      if (!saved) return Array.from({length: TOTAL_SERVERS}, (_, i) => i);
      const all = Array.from({length: TOTAL_SERVERS}, (_,i) => i);
      const missing = all.filter(i => !saved.includes(i));
      return missing.length > 0 ? [...saved, ...missing] : saved;
    } catch { return Array.from({length: TOTAL_SERVERS}, (_, i) => i); }
  });
  const [data, setData] = useState(null);
  // [D-430] Черга у шапці. У демо показуємо роботу, а не порожній простій:
  // «нуль у черзі» на вітрині читається як «продуктом ніхто не користується».
  const demoTop = useDemo();
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState({});  // D-276: per-server worker on/off стан
  const [coreMap, setCoreMap] = useState({});  // Hetzner-тип/диск для зміни типу (test/staging)

  useEffect(() => {
    const load = () => {
      getSystem().then(r => { setData(r.data); setLoading(false); });
      getWorkerStatus().then(r => setWorkers(r.data || {})).catch(() => {});
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { getCoreServers().then(r => { const m = {}; (r.data || []).forEach(x => { m[x.name] = x; }); setCoreMap(m); }).catch(() => {}); }, []);

  const onSetWorker = (serverKey, mode) => {
    setWorkers(w => ({ ...w, [serverKey]: { ...(w[serverKey] || {}), mode } }));  // оптимістично
    setWorkerMode(serverKey, mode).finally(() =>
      setTimeout(() => getWorkerStatus().then(r => setWorkers(r.data || {})).catch(() => {}), 700));
  };
  const onSetConc = (ip, val) => {
    setWorkerConcurrency(ip, val).then(() => setTimeout(() => getSystem().then(r => setData(r.data)).catch(()=>{}), 2000)).catch(e => alert('Помилка: ' + (e.response?.data?.detail || e.message)));
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }}/></Box>;

  const qView = demoTop ? { ...data, ...fakeQueue() } : data;
  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#fff', mb: 1, fontWeight: 'bold' }}>⚙️ Система</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display:'flex', gap:2 }}>
          <Box sx={{ bgcolor: qView?.celery_queued > 0 ? 'rgba(var(--ac1rgb, 124, 77, 255),0.8)' : 'rgba(var(--ac1rgb, 124, 77, 255),0.2)', borderRadius:2, p:1.5, textAlign:'center', minWidth:80 }}>
            <Typography variant="caption" sx={{ color:'#ccc', display:'block' }}>📦 Celery черга</Typography>
            <Typography variant="caption" sx={{ color:'#fff', fontWeight:'bold', display:'block' }}>{qView?.celery_queued || 0}</Typography>
          </Box>
          {qView?.versions_processing > 0 && <Box sx={{ bgcolor:'rgba(255,152,0,0.8)', borderRadius:2, p:1.5, textAlign:'center', minWidth:80 }}>
            <Typography variant="caption" sx={{ color:'#ccc', display:'block' }}>🎬 Версій в обробці</Typography>
            <Typography variant="caption" sx={{ color:'#fff', fontWeight:'bold', display:'block' }}>{qView?.versions_processing}</Typography>
          </Box>}
        </Box>
        <Chip label={data?.redis_connected ? '🟢 Redis OK' : '🔴 Redis DOWN'}
          sx={{ bgcolor: data?.redis_connected ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                color: data?.redis_connected ? '#4caf50' : '#f44336' }}/>
        <Chip label="🔄 Оновлення: 5 сек" sx={{ bgcolor: 'rgba(33,150,243,0.2)', color: '#2196f3' }}/>
        <DedicatedServers section="controls" />
      </Box>

      <Grid container spacing={2}>
        {sysOrder.map((idx, i) => {
          const servers = [
            { key:"n8n_server", title:"🖥 n8n-server", ip:"46.62.194.196", data:data?.n8n_server, accent:"#7c4dff" },
            { key:"test_server", title:"📊 test-server", ip:"77.42.21.94", data:data?.test_server, accent:"#4caf50", hname:"test-server" },
            { key:"staging_server", title:"🟩 staging-server", ip:"178.105.111.39", data:data?.staging_server, accent:"#00bcd4", hname:"staging-server" },
            { key:null, title:"🔮 apps-1 (ZODIAQ)", ip:"167.233.172.34", data:data?.apps1_server, accent:"#ffd479" },
            // [D-422] worker-5 повернувся у спільну лінію — перед dyd-prod
            // (вимога OWNER). Метрики беремо з `video_worker`: це той самий
            // `instance="worker-5"` у Prometheus, просто історична назва поля.
            // Нижче, у «Дедикованих воркерах», лишається його керування
            // (concurrency, черги) — тут лише показники машини.
            { key:null, title:"\u2699\ufe0f worker-5", ip:"10.0.0.6", data:data?.video_worker, accent:"#facc15" },
            { key:null, title:"🗄 vault-1 (сховище копій + Finorba)", ip:"49.13.71.183", data:data?.dyd_server, accent:"#ff80ab" },
          ];
          const s = servers[idx];
          if (!s) return null;  // self-heal: сумісність зі збереженим sys_order у localStorage
          return (
            <Grid item xs={12} md={3} key={idx}
              draggable onDragStart={() => window._dragFrom = i}
              onDragOver={e => e.preventDefault()}
              onDrop={() => { if (window._dragFrom !== undefined) { const o=[...sysOrder]; const [m]=o.splice(window._dragFrom,1); o.splice(i,0,m); setSysOrder(o); localStorage.setItem('sys_order',JSON.stringify(o)); }}}>
              <ServerCard title={s.title} ip={s.ip} data={s.data} accent={s.accent}
                serverKey={s.key} worker={workers[s.key]} onSet={onSetWorker} onConc={onSetConc}
                hname={s.hname} stype={s.hname ? coreMap[s.hname]?.stype : null} disk={s.hname ? coreMap[s.hname]?.disk : null}/>
            </Grid>
          );
        })}
      </Grid>
      <DedicatedServers section="cards" />
    </Box>
  );
}
